/**
 * ProviderKeyCard — One card per LLM provider on the API Keys settings
 * page. Shows configured/not-configured status plus the action buttons
 * to add / replace / test / remove.
 *
 * AC-1514 (epic AC-1493 Phase 7A). Rendered by `ApiKeysSettingsPage` once
 * per provider in the documented list (OpenAI, Anthropic, Google).
 *
 * ## States
 *
 *   - Not configured: status badge "Not configured" + single "Add key"
 *     button. If `comingSoon` is set, the button is disabled with a
 *     tooltip (Google is the AC-1493 Phase 7A "Coming soon" provider).
 *   - Configured (user key): status badge "Configured" + label + last
 *     validated timestamp + "Replace", "Test", "Remove" buttons.
 *   - Configured (platform key) — AC-1619: when the deployment supplies the
 *     provider's key via the environment (`platform_configured`) and the user
 *     has no personal key, the badge reads "Configured" with a "platform key"
 *     sub-label. No management buttons are shown because the runtime ignores
 *     per-user keys in this mode.
 *
 * ## BYOK vs. Friends & Family (AC-1619)
 *
 * `byokMode` reflects the deployment runtime mode. When `byokMode === false`
 * (F&F / platform-managed), per-user keys are ignored by the runtime, so the
 * Add / Replace / Test / Remove CTAs are hidden entirely — there is nothing
 * actionable for the user to do. When `byokMode === true` (default), the card
 * behaves as before.
 *
 * The card delegates the actual mutation to its parent via `onAdd`,
 * `onReplace`, `onTest`, `onRemove`. The parent decides which modal/
 * confirmation to show. This keeps the card a pure render layer.
 */

import React from 'react';
import { CheckCircle2, AlertCircle, Loader2, KeyRound } from 'lucide-react';
import { LLMProvider, UserApiKey } from '../../types';

export interface ProviderKeyCardProps {
  provider: LLMProvider;
  providerLabel: string;
  /** The user's stored row for this provider, if any. */
  userKey?: UserApiKey | null;
  /** When true, all action buttons are disabled and a "Coming soon" tooltip is shown. */
  comingSoon?: boolean;
  /** When true, the Test button shows a spinner. */
  isTestPending?: boolean;
  /**
   * AC-1619: Deployment runtime mode. When false (Friends & Family /
   * platform-managed), per-user keys are ignored by the runtime so all
   * management CTAs are hidden. Defaults to true (BYOK) to preserve the
   * legacy single-card behavior for callers that don't pass it yet.
   */
  byokMode?: boolean;
  onAdd: () => void;
  onReplace: () => void;
  onTest: () => void;
  onRemove: () => void;
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return 'never';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'never';
    return d.toLocaleString();
  } catch {
    return 'never';
  }
}

const ProviderKeyCard: React.FC<ProviderKeyCardProps> = ({
  provider,
  providerLabel,
  userKey,
  comingSoon = false,
  isTestPending = false,
  byokMode = true,
  onAdd,
  onReplace,
  onTest,
  onRemove,
}) => {
  const hasUserKey = Boolean(userKey?.has_key);
  const platformConfigured = Boolean(userKey?.platform_configured);
  // AC-1619: a provider counts as configured if the user supplied a key OR the
  // deployment supplies one via the environment (platform-managed).
  const isConfigured = hasUserKey || platformConfigured;
  // Platform-only: configured purely by the deployment env key, no user key.
  const isPlatformOnly = platformConfigured && !hasUserKey;
  // In F&F (byokMode === false) per-user keys are ignored, so there's nothing
  // for the user to add / replace / test / remove.
  const showActions = byokMode;

  return (
    <div
      data-testid={`provider-key-card-${provider}`}
      data-provider={provider}
      className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6"
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center min-w-0">
          <KeyRound
            className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-3 flex-shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
              {providerLabel}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
              {isPlatformOnly
                ? 'Provided by the platform (environment-managed).'
                : hasUserKey && userKey?.display_label
                  ? `Label: ${userKey.display_label}`
                  : hasUserKey
                    ? 'No label set'
                    : comingSoon
                      ? 'Support coming soon.'
                      : 'No key configured for this provider yet.'}
            </p>
            {isPlatformOnly && (
              <p
                data-testid={`provider-key-card-${provider}-platform`}
                className="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
              >
                Using platform key
              </p>
            )}
            {hasUserKey && (
              <p
                data-testid={`provider-key-card-${provider}-last-validated`}
                className="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
              >
                Last validated: {formatTimestamp(userKey?.last_validated_at)}
              </p>
            )}
          </div>
        </div>

        <span
          data-testid={`provider-key-card-${provider}-status`}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
            isConfigured
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
          }`}
        >
          {isConfigured ? (
            <>
              <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
              Configured
            </>
          ) : (
            <>
              <AlertCircle className="w-3 h-3 mr-1" aria-hidden="true" />
              Not configured
            </>
          )}
        </span>
      </div>

      {showActions && (
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!hasUserKey ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={comingSoon}
            title={comingSoon ? 'Coming soon' : undefined}
            data-testid={`provider-key-card-${provider}-add`}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add key
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onReplace}
              disabled={comingSoon}
              data-testid={`provider-key-card-${provider}-replace`}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onTest}
              disabled={comingSoon || isTestPending}
              data-testid={`provider-key-card-${provider}-test`}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center"
            >
              {isTestPending && (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />
              )}
              Test
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={comingSoon}
              data-testid={`provider-key-card-${provider}-remove`}
              className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
            >
              Remove
            </button>
          </>
        )}
      </div>
      )}
    </div>
  );
};

export default ProviderKeyCard;
